import { Component, OnInit, computed, inject, viewChild, ChangeDetectionStrategy, DestroyRef, Signal } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatDrawer, MatDrawerMode, MatSidenavModule } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { NgOptimizedImage } from '@angular/common';
import { ThemeLayoutService } from '@services/theme-layout.service';
import { BreadcrumbComponent } from '@app/shared/components/ui';
import { AuthenticationService } from '@app/core/auth';
import { MIN_WIDTH_1025PX, MAX_WIDTH_1024PX } from '@core/const';
import { EnvironmentService } from '@core/services/environment.service';
import { Navigation } from '@app/core/types/navigation.interface';
import { Role } from '@app/core/types/role.interface';
import { HelpLinkService } from '@app/core/services/help-link.service';
import { NavBarComponent } from '../components/header/app-header.component';
import { VerticalMenuComponent } from '../components/sidebar/vertical-menu/vertical-menu.component';
import { ConfigurationComponent } from '../components/configuration/configuration.component';
import { FooterComponent } from '../components/footer/footer.component';
import { IntroService } from '@app/core/infrastructure/third-party';
import { NotificationService } from '@app/core/infrastructure/notifications';
import { filter } from 'rxjs';
import { MENU_INTERNAL_ROUTES } from '@app/core/const';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    RouterModule,
    NgOptimizedImage,
    BreadcrumbComponent,
    NavBarComponent,
    VerticalMenuComponent,
    ConfigurationComponent,
    FooterComponent,
    MatSidenavModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MainLayoutComponent implements OnInit {
   private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly themeService = inject(ThemeLayoutService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly router = inject(Router);
  private readonly notificationService = inject(NotificationService);
  private readonly introService = inject(IntroService);
  private readonly destroyRef = inject(DestroyRef);
  readonly authService = inject(AuthenticationService);
  readonly helpLinkService = inject(HelpLinkService);
  readonly sidebar = viewChild.required<MatDrawer>('sidebar');

  modeValue: MatDrawerMode = 'side';
  drawerOpened = false;
  currentApplicationVersion = this.environmentService.appVersion;

  private readonly defaultLogoUrl = computed(() =>
    this.themeService.theme() === 'dark' ? 'assets/images/logo-white.svg' : 'assets/images/logo-dark.svg'
  );

  readonly companyLogoUrl = computed(() =>
    this.authService.currentUser()?.user?.company?.logoUrl || this.defaultLogoUrl()
  );

  readonly menus: Signal<Navigation[]> = computed(() => this.computeUserMenus());

  ngOnInit(): void {
    this.subscribeToLayoutChanges();
    this.subscribeToRouteChanges();

    if (this.authService.currentUser()) {
      this.notificationService.refreshNotifications().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  private subscribeToLayoutChanges(): void {
    this.breakpointObserver
      .observe([MIN_WIDTH_1025PX, MAX_WIDTH_1024PX])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.modeValue = result.breakpoints[MAX_WIDTH_1024PX] ? 'over' : 'side';
      });

    this.themeService.dashBoardMenuState
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.sidebar().toggle();
      });
  }

  private subscribeToRouteChanges(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const user = this.authService.currentUser()?.user;
        if (user) {
          this.notificationService.checkAndShow(user.id, user.name, user.company?.id ?? '');
        }
      });
  }

  onDrawerOpened(opened: boolean): void {
    this.drawerOpened = opened;
    if (opened && !this.introService.hasWelcomeTourBeenShown()) {
      setTimeout(() => this.introService.startWelcomeTour(), 100);
    }
  }

  private computeUserMenus(): Navigation[] {
    const currentUser = this.authService.currentUser();
    if (!currentUser?.user) return [];

    const userRoles = currentUser.user.role ? [currentUser.user.role] : [Role.Admin];
    const rawMenus = currentUser.user.menus as Navigation[] ?? [];

    const filteredMenus = this.filterMenuByRole(rawMenus, userRoles);
    return this.applyInternalRouteMap(filteredMenus);
  }

  private filterMenuByRole(menus: Navigation[], userRoles: string[], parentRoles: string[] = []): Navigation[] {
    return menus.reduce((acc, item) => {
      const newItem = { ...item };
      const itemRoles = newItem.role ?? parentRoles;
      const hasAccess = itemRoles.some(role => userRoles.includes(role));

      if (hasAccess) {
        if (newItem.children?.length) {
          newItem.children = this.filterMenuByRole(newItem.children, userRoles, itemRoles);
        }
        acc.push(newItem);
      }
      return acc;
    }, [] as Navigation[]);
  }

  private applyInternalRouteMap(items: Navigation[]): Navigation[] {
    return items.map(item => {
      const mappedItem: Navigation = { ...item };
      const internalRoute = MENU_INTERNAL_ROUTES[mappedItem.id];
      if (internalRoute) {
        mappedItem.url = internalRoute;
      }
      if (mappedItem.children?.length) {
        mappedItem.children = this.applyInternalRouteMap(mappedItem.children);
      }
      return mappedItem;
    });
  }
}

